// src/core/types.ts
var DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" }
];

// src/signaling/FirebaseSignalingAdapter.ts
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDoc
} from "firebase/firestore";
var ROOMS_COLLECTION = "webrtc_rooms";
async function sha256hex(text) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
function randomToken() {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 32);
}
function docToParticipant(id, data) {
  return {
    id,
    displayName: data.displayName ?? "Unknown",
    role: data.role ?? "participant",
    isLocal: false,
    isGuest: data.isGuest ?? false,
    audioEnabled: data.audioEnabled ?? true,
    videoEnabled: data.videoEnabled ?? true,
    connectionState: "new",
    joinedAt: typeof data.joinedAt?.toMillis === "function" ? data.joinedAt.toMillis() : void 0,
    sessionId: typeof data.sessionId === "string" ? data.sessionId : void 0,
    streams: []
  };
}
var FirebaseSignalingAdapter = class {
  constructor(db) {
    /** Cleanup for all active listeners */
    this.unsubscribers = /* @__PURE__ */ new Set();
    this.db = db;
    this.startTime = Date.now();
  }
  // ── Room lifecycle ──────────────────────────────────────────────────────────
  async createRoom(roomId, hostId) {
    const roomRef = doc(this.db, ROOMS_COLLECTION, roomId);
    await setDoc(
      roomRef,
      {
        hostId,
        status: "waiting",
        createdAt: serverTimestamp()
      },
      { merge: true }
    );
  }
  async joinRoom(roomId, participantId, displayName, role = "participant", sessionId) {
    const participantRef = doc(
      this.db,
      ROOMS_COLLECTION,
      roomId,
      "participants",
      participantId
    );
    await setDoc(participantRef, {
      displayName,
      role,
      isGuest: role === "guest",
      audioEnabled: true,
      videoEnabled: true,
      active: true,
      joinedAt: serverTimestamp(),
      ...sessionId ? { sessionId } : {}
    });
    const roomRef = doc(this.db, ROOMS_COLLECTION, roomId);
    await updateDoc(roomRef, { status: "active" }).catch(() => {
    });
    const col = collection(this.db, ROOMS_COLLECTION, roomId, "participants");
    const snap = await getDocs(query(col, where("active", "==", true)));
    return snap.docs.filter((d) => d.id !== participantId).map((d) => docToParticipant(d.id, d.data()));
  }
  async leaveRoom(roomId, participantId, sessionId) {
    const participantRef = doc(
      this.db,
      ROOMS_COLLECTION,
      roomId,
      "participants",
      participantId
    );
    await updateDoc(participantRef, { active: false }).catch(() => {
    });
    const signalsCol = collection(this.db, ROOMS_COLLECTION, roomId, "signals");
    const mine = await getDocs(query(signalsCol, where("from", "==", participantId)));
    const ours = mine.docs.filter((d) => !sessionId || !d.data().fromSession || d.data().fromSession === sessionId);
    await Promise.all(ours.map((d) => deleteDoc(d.ref)));
  }
  // ── Signaling ───────────────────────────────────────────────────────────────
  async sendSignal(roomId, signal) {
    const signalsCol = collection(this.db, ROOMS_COLLECTION, roomId, "signals");
    await addDoc(signalsCol, {
      ...signal,
      createdAt: serverTimestamp()
    });
  }
  onSignal(roomId, participantId, handler) {
    const signalsCol = collection(this.db, ROOMS_COLLECTION, roomId, "signals");
    const q = query(
      signalsCol,
      where("to", "in", [participantId, "broadcast"]),
      orderBy("createdAt", "asc")
    );
    console.log("[Signaling] onSignal: subscribing for", participantId, "in", roomId);
    const unsub = onSnapshot(
      q,
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type !== "added") return;
          const data = change.doc.data();
          const ts = data.createdAt?.toMillis?.() ?? 0;
          if (ts > 0 && ts < this.startTime - 5e3) return;
          console.log("[Signaling] received signal:", data.type, "from:", data.from, "to:", data.to);
          handler({
            id: change.doc.id,
            from: data.from,
            to: data.to,
            type: data.type,
            payload: data.payload,
            timestamp: ts,
            ...typeof data.fromSession === "string" ? { fromSession: data.fromSession } : {},
            ...typeof data.toSession === "string" ? { toSession: data.toSession } : {}
          });
        });
      },
      (error) => {
        console.error(
          "[Signaling] signals listener FAILED:",
          error.code,
          error.message
        );
      }
    );
    this.unsubscribers.add(unsub);
    return () => {
      unsub();
      this.unsubscribers.delete(unsub);
    };
  }
  // ── Participant presence ────────────────────────────────────────────────────
  onParticipantsChanged(roomId, handler) {
    const col = collection(this.db, ROOMS_COLLECTION, roomId, "participants");
    const q = query(col, where("active", "==", true));
    const unsub = onSnapshot(q, (snap) => {
      handler(snap.docs.map((d) => docToParticipant(d.id, d.data())));
    });
    this.unsubscribers.add(unsub);
    return () => {
      unsub();
      this.unsubscribers.delete(unsub);
    };
  }
  async updateParticipantState(roomId, participantId, state) {
    const ref = doc(this.db, ROOMS_COLLECTION, roomId, "participants", participantId);
    await updateDoc(ref, state);
  }
  // ── Guest tokens ────────────────────────────────────────────────────────────
  async createGuestToken(roomId) {
    const token = randomToken();
    const hash = await sha256hex(token);
    const roomRef = doc(this.db, ROOMS_COLLECTION, roomId);
    const snap = await getDoc(roomRef);
    if (!snap.exists()) throw new Error(`Room ${roomId} does not exist`);
    await updateDoc(roomRef, { guestTokenHash: hash });
    return `${roomId}:${token}`;
  }
  async validateGuestToken(token) {
    const colonIdx = token.indexOf(":");
    if (colonIdx === -1) return null;
    const roomId = token.slice(0, colonIdx);
    const rawToken = token.slice(colonIdx + 1);
    const roomRef = doc(this.db, ROOMS_COLLECTION, roomId);
    const snap = await getDoc(roomRef);
    if (!snap.exists()) return null;
    const stored = snap.data()?.guestTokenHash;
    if (!stored) return null;
    const hash = await sha256hex(rawToken);
    return hash === stored ? roomId : null;
  }
  /** Unsubscribe all active listeners. Call this when tearing down. */
  destroy() {
    this.unsubscribers.forEach((u) => u());
    this.unsubscribers.clear();
  }
};

// src/signaling/WebSocketSignalingAdapter.ts
var WebSocketSignalingAdapter = class {
  constructor(url, reconnectMs = 3e3) {
    this.ws = null;
    this.signalHandlers = /* @__PURE__ */ new Map();
    this.participantHandlers = /* @__PURE__ */ new Map();
    this.pendingJoin = null;
    this.pendingToken = null;
    this.pendingValidate = null;
    this.reconnectTimer = null;
    this.destroyed = false;
    this.url = url;
    this.reconnectMs = reconnectMs;
    this.connect();
  }
  connect() {
    if (this.destroyed) return;
    this.ws = new WebSocket(this.url);
    this.ws.onmessage = this.handleMessage.bind(this);
    this.ws.onclose = () => {
      if (!this.destroyed) {
        this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectMs);
      }
    };
  }
  send(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("WebSocket not connected");
    }
    this.ws.send(JSON.stringify(data));
  }
  handleMessage(event) {
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }
    switch (msg.type) {
      case "joined":
        this.pendingJoin?.resolve(msg.participants ?? []);
        this.pendingJoin = null;
        break;
      case "participants":
        this.participantHandlers.forEach((h) => h(msg.participants));
        break;
      case "signal": {
        const sig = msg.signal;
        this.signalHandlers.forEach((h) => h(sig));
        break;
      }
      case "guest-token":
        this.pendingToken?.resolve(msg.token);
        this.pendingToken = null;
        break;
      case "validate-token":
        this.pendingValidate?.resolve(msg.roomId ?? null);
        this.pendingValidate = null;
        break;
      case "error":
        this.pendingJoin?.reject(new Error(msg.message));
        this.pendingJoin = null;
        break;
    }
  }
  // ── SignalingAdapter implementation ─────────────────────────────────────────
  async createRoom(roomId, hostId) {
    this.send({ type: "create-room", roomId, hostId });
  }
  joinRoom(roomId, participantId, displayName, role = "participant") {
    return new Promise((resolve, reject) => {
      this.pendingJoin = { resolve, reject };
      this.send({ type: "join", roomId, participantId, displayName, role });
    });
  }
  async leaveRoom(roomId, participantId) {
    this.send({ type: "leave", roomId, participantId });
  }
  async sendSignal(roomId, signal) {
    this.send({ type: "signal", roomId, signal });
  }
  onSignal(_roomId, participantId, handler) {
    this.signalHandlers.set(participantId, handler);
    return () => this.signalHandlers.delete(participantId);
  }
  onParticipantsChanged(_roomId, handler) {
    const key = Math.random().toString(36).slice(2);
    this.participantHandlers.set(key, handler);
    return () => this.participantHandlers.delete(key);
  }
  async updateParticipantState(roomId, participantId, state) {
    this.send({ type: "update", roomId, participantId, state });
  }
  createGuestToken(roomId) {
    return new Promise((resolve, reject) => {
      this.pendingToken = { resolve, reject };
      this.send({ type: "guest-token", roomId });
    });
  }
  validateGuestToken(token) {
    return new Promise((resolve, reject) => {
      this.pendingValidate = { resolve, reject };
      this.send({ type: "validate-token", token });
    });
  }
  destroy() {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
  }
};

// src/core/RTCSessionManager.ts
var TypedEmitter = class {
  constructor() {
    this.listeners = /* @__PURE__ */ new Map();
  }
  on(event, handler) {
    const key = event;
    if (!this.listeners.has(key)) this.listeners.set(key, /* @__PURE__ */ new Set());
    this.listeners.get(key).add(handler);
    return () => this.listeners.get(key)?.delete(handler);
  }
  emit(event, ...args) {
    this.listeners.get(event)?.forEach((h) => h(...args));
  }
  off(event, handler) {
    this.listeners.get(event)?.delete(handler);
  }
  removeAllListeners() {
    this.listeners.clear();
  }
};
var CANDIDATE_BATCH_MS = 40;
var DATA_CHANNEL_LABEL = "db-room";
var DATA_CHANNEL_ID = 0;
var DATA_CHANNEL_HIGH_WATER = 1 << 20;
function newSessionId() {
  const c = globalThis.crypto;
  return c?.randomUUID ? c.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
var RTCSessionManager = class extends TypedEmitter {
  constructor(localParticipantId, adapter, config = {}) {
    super();
    this.roomId = null;
    /**
     * This join's session, new each joinRoom. It goes on our participant entry
     * and on every signal we send, and the others address their signals to it,
     * so a signal meant for an earlier session of ours (before a rejoin) is
     * dropped on arrival, with nothing to wait for.
     */
    this.sessionId = null;
    /** Signals from each peer, handled one at a time in the order they came. */
    this.signalChains = /* @__PURE__ */ new Map();
    this.localStream = null;
    this.sharedSources = /* @__PURE__ */ new Map();
    this.peers = /* @__PURE__ */ new Map();
    this.signalUnsub = null;
    this.participantsUnsub = null;
    this.localParticipantId = localParticipantId;
    this.adapter = adapter;
    this.config = {
      iceServers: config.iceServers ?? DEFAULT_ICE_SERVERS,
      maxParticipants: config.maxParticipants ?? 8,
      enableAudio: config.enableAudio ?? true,
      enableVideo: config.enableVideo ?? true
    };
  }
  // ── Public API ───────────────────────────────────────────────────────────────
  /** Acquires local media and joins the room. */
  async joinRoom(roomId, displayName, role = "participant", localStream) {
    this.roomId = roomId;
    if (localStream) {
      this.localStream = localStream;
    } else {
      this.localStream = await this.acquireLocalMedia();
    }
    this.sessionId = newSessionId();
    this.signalChains.clear();
    console.log("[RTC] joinRoom:", roomId, "as", role, "localParticipantId:", this.localParticipantId, "session:", this.sessionId);
    this.signalUnsub = this.adapter.onSignal(
      roomId,
      this.localParticipantId,
      (signal) => this.receiveSignal(signal)
    );
    this.participantsUnsub = this.adapter.onParticipantsChanged(
      roomId,
      (participants) => this.reconcileParticipants(participants)
    );
    const existing = await this.adapter.joinRoom(roomId, this.localParticipantId, displayName, role, this.sessionId);
    console.log("[RTC] existing participants on join:", existing.length, existing.map((p) => p.id));
    for (const participant of existing) {
      this.getOrCreatePeer(participant);
    }
  }
  async leaveRoom() {
    if (!this.roomId) return;
    this.signalUnsub?.();
    this.participantsUnsub?.();
    this.signalUnsub = null;
    this.participantsUnsub = null;
    for (const [peerId, state] of this.peers) {
      state.pc.close();
      this.peers.delete(peerId);
    }
    const roomId = this.roomId;
    const sessionId = this.sessionId ?? void 0;
    this.roomId = null;
    this.sessionId = null;
    await this.adapter.leaveRoom(roomId, this.localParticipantId, sessionId);
    this.signalChains.clear();
  }
  /** Replaces the local media stream (e.g. after toggling camera). */
  async setLocalStream(stream) {
    this.localStream = stream;
    for (const { pc } of this.peers.values()) {
      const senders = pc.getSenders();
      for (const track of stream.getTracks()) {
        const existing = senders.find((s) => s.track?.kind === track.kind);
        if (existing) {
          await existing.replaceTrack(track);
        } else {
          pc.addTrack(track, stream);
        }
      }
    }
  }
  /** Enables or disables the local audio track. */
  setAudioEnabled(enabled) {
    this.localStream?.getAudioTracks().forEach((t) => t.enabled = enabled);
    if (this.roomId) {
      this.adapter.updateParticipantState(this.roomId, this.localParticipantId, {
        audioEnabled: enabled
      });
    }
  }
  /** Enables or disables the local video track. */
  setVideoEnabled(enabled) {
    this.localStream?.getVideoTracks().forEach((t) => t.enabled = enabled);
    if (this.roomId) {
      this.adapter.updateParticipantState(this.roomId, this.localParticipantId, {
        videoEnabled: enabled
      });
    }
  }
  /** Adds a shared source (screen share, canvas stream, etc.) to all peers. */
  async addSharedSource(sourceId, kind, stream, label) {
    const source = { id: sourceId, kind, label, stream };
    this.sharedSources.set(sourceId, source);
    for (const { pc } of this.peers.values()) {
      for (const track of stream.getTracks()) {
        pc.addTrack(track, stream);
      }
    }
    for (const peerId of this.peers.keys()) {
      await this.negotiate(peerId);
    }
    this.emit("sharedSourceAdded", source);
  }
  /** Removes a shared source from all peers. */
  async removeSharedSource(sourceId) {
    const source = this.sharedSources.get(sourceId);
    if (!source) return;
    const trackIds = new Set(source.stream.getTracks().map((t) => t.id));
    for (const { pc } of this.peers.values()) {
      const senders = pc.getSenders().filter((s) => s.track && trackIds.has(s.track.id));
      for (const sender of senders) {
        pc.removeTrack(sender);
      }
    }
    source.stream.getTracks().forEach((t) => t.stop());
    this.sharedSources.delete(sourceId);
    for (const peerId of this.peers.keys()) {
      await this.negotiate(peerId);
    }
    this.emit("sharedSourceRemoved", sourceId);
  }
  get localStream$() {
    return this.localStream;
  }
  /**
   * Sends one JSON message on the data channel to every connected peer, or
   * to one. Returns how many peers it went to; a peer whose channel is not
   * open, or is backed up past the high-water mark, is skipped.
   */
  sendData(data, to) {
    const text = JSON.stringify(data);
    let sent = 0;
    for (const [id, state] of this.peers) {
      if (to && id !== to) continue;
      const ch = state.channel;
      if (!ch || ch.readyState !== "open" || ch.bufferedAmount > DATA_CHANNEL_HIGH_WATER) continue;
      try {
        ch.send(text);
        sent += 1;
      } catch (err) {
        console.warn("[RTC] data send failed to", id, err);
      }
    }
    return sent;
  }
  /** Whether a peer's data channel is open (any peer when none is named). */
  isDataChannelOpen(to) {
    for (const [id, state] of this.peers) {
      if (to && id !== to) continue;
      if (state.channel?.readyState === "open") return true;
    }
    return false;
  }
  destroy() {
    void this.leaveRoom();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.sharedSources.forEach((s) => s.stream.getTracks().forEach((t) => t.stop()));
    this.removeAllListeners();
  }
  // ── Private: peer connection lifecycle ──────────────────────────────────────
  getOrCreatePeer(participant) {
    const existing = this.peers.get(participant.id);
    if (existing) return existing;
    const pc = new RTCPeerConnection({ iceServers: this.config.iceServers });
    const state = {
      pc,
      participant: { ...participant, streams: [], connectionState: "new" },
      makingOffer: false,
      ignoreOffer: false,
      isPolite: this.localParticipantId < participant.id,
      respondingToOffer: false,
      pendingCandidates: [],
      channel: null,
      outCandidates: [],
      outTimer: null
    };
    this.peers.set(participant.id, state);
    try {
      const channel = pc.createDataChannel(DATA_CHANNEL_LABEL, { negotiated: true, id: DATA_CHANNEL_ID, ordered: true });
      channel.onopen = () => this.emit("dataChannelOpen", participant.id);
      channel.onmessage = ({ data }) => {
        if (typeof data !== "string") return;
        try {
          this.emit("dataMessage", participant.id, JSON.parse(data));
        } catch {
        }
      };
      state.channel = channel;
    } catch (err) {
      console.warn("[RTC] data channel unavailable for", participant.id, err);
    }
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        pc.addTrack(track, this.localStream);
      }
    }
    for (const source of this.sharedSources.values()) {
      for (const track of source.stream.getTracks()) {
        pc.addTrack(track, source.stream);
      }
    }
    const sendCandidates = () => {
      if (state.outTimer) {
        clearTimeout(state.outTimer);
        state.outTimer = null;
      }
      const batch = state.outCandidates.splice(0);
      if (!batch.length || !this.roomId || this.peers.get(participant.id) !== state) return;
      void this.adapter.sendSignal(this.roomId, {
        from: this.localParticipantId,
        to: participant.id,
        type: "ice-candidate",
        payload: { ...batch[0], candidates: batch },
        ...this.sessions(state)
      });
    };
    pc.onicecandidate = ({ candidate }) => {
      if (!this.roomId) return;
      if (!candidate) {
        sendCandidates();
        return;
      }
      console.log("[RTC] ICE candidate sending to", participant.id, "type:", candidate.type, "protocol:", candidate.protocol);
      state.outCandidates.push(candidate.toJSON());
      if (!state.outTimer) state.outTimer = setTimeout(sendCandidates, CANDIDATE_BATCH_MS);
    };
    pc.onicegatheringstatechange = () => {
      console.log("[RTC] ICE gathering state:", pc.iceGatheringState, "for", participant.id);
    };
    pc.oniceconnectionstatechange = () => {
      console.log("[RTC] ICE connection state:", pc.iceConnectionState, "for", participant.id);
    };
    pc.onnegotiationneeded = async () => {
      console.log("[RTC] onnegotiationneeded for", participant.id);
      await this.negotiate(participant.id);
    };
    pc.onconnectionstatechange = () => {
      console.log("[RTC] connectionState ->", pc.connectionState, "for", participant.id);
      if (this.peers.get(participant.id) !== state) return;
      const pState = pc.connectionState;
      state.participant = { ...state.participant, connectionState: pState };
      this.peers.set(participant.id, state);
      this.emit("connectionStateChanged", participant.id, pState);
      this.emit("participantUpdated", state.participant);
      if (pState === "failed" || pState === "closed") {
        this.peers.delete(participant.id);
        this.emit("participantLeft", participant.id);
      }
    };
    pc.ontrack = ({ track, streams }) => {
      if (this.peers.get(participant.id) !== state) return;
      for (const stream of streams) {
        if (!state.participant.streams.find((s) => s.id === stream.id)) {
          state.participant = {
            ...state.participant,
            streams: [...state.participant.streams, stream]
          };
          this.peers.set(participant.id, state);
          this.emit("streamAdded", participant.id, stream);
          this.emit("participantUpdated", state.participant);
        }
      }
      track.onended = () => {
        if (this.peers.get(participant.id) !== state) return;
        state.participant = {
          ...state.participant,
          streams: state.participant.streams.filter(
            (s) => s.getTracks().some((t) => t.readyState === "live")
          )
        };
        this.peers.set(participant.id, state);
        this.emit("participantUpdated", state.participant);
      };
    };
    this.emit("participantJoined", state.participant);
    return state;
  }
  // ── Private: perfect negotiation ─────────────────────────────────────────────
  async negotiate(peerId) {
    const state = this.peers.get(peerId);
    if (!state || !this.roomId) return;
    if (state.makingOffer) return;
    if (state.respondingToOffer) return;
    console.log("[RTC] negotiate: sending offer to", peerId, "(polite:", state.isPolite, ")");
    try {
      state.makingOffer = true;
      await state.pc.setLocalDescription();
      if (!state.pc.localDescription || !this.roomId) return;
      if (state.pc.signalingState !== "have-local-offer" || state.respondingToOffer) return;
      await this.adapter.sendSignal(this.roomId, {
        from: this.localParticipantId,
        to: peerId,
        type: "offer",
        payload: { sdp: state.pc.localDescription.toJSON() },
        ...this.sessions(state)
      });
    } catch (err) {
      this.emit("error", err instanceof Error ? err : new Error(String(err)));
    } finally {
      state.makingOffer = false;
    }
  }
  /** The sessions a signal to this peer carries: ours, and theirs once known. */
  sessions(state) {
    return {
      ...this.sessionId ? { fromSession: this.sessionId } : {},
      ...state.participant.sessionId ? { toSession: state.participant.sessionId } : {}
    };
  }
  /**
   * A signal as it arrives: dropped if it is for another session of ours,
   * else queued behind that peer's earlier signals, so an offer's answer is
   * set before the candidates that follow it, in the order they were sent.
   */
  receiveSignal(signal) {
    if (signal.from === this.localParticipantId) return;
    if (signal.toSession && this.sessionId && signal.toSession !== this.sessionId) {
      console.log("[RTC] dropping", signal.type, "from", signal.from, "for an earlier session of ours");
      return;
    }
    const prev = this.signalChains.get(signal.from) ?? Promise.resolve();
    const next = prev.then(() => this.handleSignal(signal)).catch((err) => {
      this.emit("error", err instanceof Error ? err : new Error(String(err)));
    });
    this.signalChains.set(signal.from, next);
  }
  async handleSignal(signal) {
    const { from, type, payload } = signal;
    if (from === this.localParticipantId) return;
    console.log("[RTC] handleSignal:", type, "from:", from, "peers known:", [...this.peers.keys()]);
    if (type === "participant-joined") {
      return;
    }
    if (type === "participant-left") {
      const state2 = this.peers.get(from);
      if (state2) {
        state2.pc.close();
        this.peers.delete(from);
        this.emit("participantLeft", from);
      }
      return;
    }
    let state = this.peers.get(from);
    if (state && type === "offer" && signal.fromSession && state.participant.sessionId && signal.fromSession !== state.participant.sessionId) {
      console.log("[RTC] offer from a new session of", from);
      const participant = { ...state.participant, sessionId: signal.fromSession };
      this.peers.delete(from);
      state.pc.close();
      this.emit("participantLeft", from);
      state = this.getOrCreatePeer(participant);
    }
    if (!state) {
      if (type !== "offer") return;
      this.getOrCreatePeer({
        id: from,
        displayName: "Unknown",
        role: "participant",
        isLocal: false,
        isGuest: false,
        audioEnabled: true,
        videoEnabled: true,
        connectionState: "new",
        streams: [],
        ...signal.fromSession ? { sessionId: signal.fromSession } : {}
      });
      state = this.peers.get(from);
    }
    const pc = state.pc;
    try {
      if (type === "offer") {
        const offer = payload.sdp;
        const collision = state.makingOffer || pc.signalingState !== "stable";
        state.ignoreOffer = !state.isPolite && collision;
        console.log("[RTC] offer from", from, "collision:", collision, "polite:", state.isPolite, "ignoreOffer:", state.ignoreOffer, "signalingState:", pc.signalingState);
        if (state.ignoreOffer) return;
        state.respondingToOffer = true;
        try {
          if (collision) console.log("[RTC] offer: collision, the remote offer rolls ours back");
          await pc.setRemoteDescription(offer);
          console.log("[RTC] offer: remote desc set, signalingState:", pc.signalingState);
          for (const candidate of state.pendingCandidates.splice(0)) {
            await pc.addIceCandidate(candidate);
          }
          await pc.setLocalDescription();
          console.log("[RTC] offer: answer created, localDesc:", pc.localDescription?.type, "roomId:", this.roomId);
          if (!pc.localDescription || !this.roomId) return;
          console.log("[RTC] sending answer to", from);
          void this.adapter.sendSignal(this.roomId, {
            from: this.localParticipantId,
            to: from,
            type: "answer",
            payload: { sdp: pc.localDescription.toJSON() },
            ...this.sessions(state)
          }).catch((err) => this.emit("error", err instanceof Error ? err : new Error(String(err))));
        } finally {
          state.respondingToOffer = false;
        }
      } else if (type === "answer") {
        const answer = payload.sdp;
        if (pc.signalingState === "have-local-offer") {
          console.log("[RTC] processing answer from", from);
          await pc.setRemoteDescription(answer);
          for (const candidate of state.pendingCandidates.splice(0)) {
            await pc.addIceCandidate(candidate);
          }
        } else {
          console.log("[RTC] ignoring answer from", from, "\u2014 signalingState:", pc.signalingState);
        }
      } else if (type === "ice-candidate") {
        const one = payload;
        const list = Array.isArray(one.candidates) ? one.candidates : [one];
        for (const c of list) {
          const parts = (c.candidate ?? "").split(" ");
          const cTypeIdx = parts.indexOf("typ");
          const cType = cTypeIdx >= 0 ? parts[cTypeIdx + 1] ?? "?" : "?";
          console.log("[RTC] ICE candidate received from", from, "type:", cType, "protocol:", parts[2] ?? "?", "addr:", parts[4] ?? "?");
          if (!pc.remoteDescription) {
            state.pendingCandidates.push(c);
          } else {
            try {
              await pc.addIceCandidate(c);
            } catch (err) {
              if (!state.ignoreOffer) {
                this.emit("error", err instanceof Error ? err : new Error(String(err)));
              }
            }
          }
        }
      }
    } catch (err) {
      this.emit("error", err instanceof Error ? err : new Error(String(err)));
    }
  }
  // ── Private: participant reconciliation ──────────────────────────────────────
  reconcileParticipants(participants) {
    console.log("[RTC] reconcileParticipants:", participants.map((p) => p.id));
    const incomingIds = new Set(participants.map((p) => p.id));
    for (const [id, state] of this.peers) {
      if (!incomingIds.has(id)) {
        state.pc.close();
        this.peers.delete(id);
        this.emit("participantLeft", id);
      }
    }
    for (const participant of participants) {
      if (participant.id === this.localParticipantId) continue;
      if (!this.peers.has(participant.id)) {
        console.log("[RTC] reconcile: new participant", participant.id);
        this.getOrCreatePeer(participant);
      } else {
        const state = this.peers.get(participant.id);
        const newSession = participant.sessionId && state.participant.sessionId ? participant.sessionId !== state.participant.sessionId : !!(participant.joinedAt && state.participant.joinedAt && participant.joinedAt > state.participant.joinedAt);
        if (newSession) {
          console.log("[RTC] reconcile: new session for", participant.id);
          this.peers.delete(participant.id);
          state.pc.close();
          this.emit("participantLeft", participant.id);
          this.getOrCreatePeer(participant);
          continue;
        }
        state.participant = {
          ...state.participant,
          displayName: participant.displayName,
          audioEnabled: participant.audioEnabled,
          videoEnabled: participant.videoEnabled,
          joinedAt: participant.joinedAt ?? state.participant.joinedAt,
          sessionId: participant.sessionId ?? state.participant.sessionId
        };
        this.emit("participantUpdated", state.participant);
      }
    }
  }
  // ── Private: media acquisition ───────────────────────────────────────────────
  async acquireLocalMedia() {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: this.config.enableAudio,
        video: this.config.enableVideo ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } : false
      });
    } catch (err) {
      if (this.config.enableAudio) {
        return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
      throw err;
    }
  }
};

// src/core/GuestToken.ts
async function hmacKey(secret) {
  const enc = new TextEncoder();
  return crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"]
  );
}
function base64url(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let str = "";
  bytes.forEach((b) => {
    str += String.fromCharCode(b);
  });
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decodeBase64url(s) {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(s.length + (4 - s.length % 4) % 4, "=");
  return atob(padded);
}
async function createGuestToken(secret, roomId, ttlMs = 24 * 60 * 60 * 1e3) {
  const payload = {
    roomId,
    issuedAt: Date.now(),
    expiresAt: Date.now() + ttlMs
  };
  const enc = new TextEncoder();
  const key = await hmacKey(secret);
  const payloadB64 = base64url(enc.encode(JSON.stringify(payload)));
  const sig = base64url(await crypto.subtle.sign("HMAC", key, enc.encode(payloadB64)));
  return `${payloadB64}.${sig}`;
}
async function validateGuestToken(secret, token) {
  const parts = token.split(".");
  if (parts.length !== 2) return null;
  const [payloadB64, sig] = parts;
  try {
    const enc = new TextEncoder();
    const key = await hmacKey(secret);
    const expectedSig = base64url(await crypto.subtle.sign("HMAC", key, enc.encode(payloadB64)));
    if (expectedSig !== sig) return null;
    const payload = JSON.parse(decodeBase64url(payloadB64));
    if (payload.expiresAt && Date.now() > payload.expiresAt) return null;
    return payload;
  } catch {
    return null;
  }
}

// src/react/WebRTCContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useCallback
} from "react";
import { jsx } from "react/jsx-runtime";
function reducer(state, action) {
  switch (action.type) {
    case "SET_LOCAL_STREAM":
      return { ...state, localStream: action.stream };
    case "PARTICIPANT_JOINED": {
      const next = new Map(state.participants);
      next.set(action.participant.id, action.participant);
      return { ...state, participants: next };
    }
    case "PARTICIPANT_UPDATED": {
      const next = new Map(state.participants);
      next.set(action.participant.id, action.participant);
      return { ...state, participants: next };
    }
    case "PARTICIPANT_LEFT": {
      const next = new Map(state.participants);
      next.delete(action.id);
      return { ...state, participants: next };
    }
    case "SHARED_SOURCE_ADDED":
      return {
        ...state,
        sharedSources: [...state.sharedSources.filter((s) => s.id !== action.source.id), action.source]
      };
    case "SHARED_SOURCE_REMOVED":
      return { ...state, sharedSources: state.sharedSources.filter((s) => s.id !== action.id) };
    case "SET_AUDIO":
      return { ...state, audioEnabled: action.enabled };
    case "SET_VIDEO":
      return { ...state, videoEnabled: action.enabled };
    case "SET_ROOM":
      return { ...state, roomId: action.roomId };
    default:
      return state;
  }
}
var WebRTCContext = createContext(null);
function WebRTCProvider({
  signalingAdapter,
  participantId,
  callConfig,
  children
}) {
  const [callState, dispatch] = useReducer(reducer, {
    localStream: null,
    participants: /* @__PURE__ */ new Map(),
    sharedSources: [],
    audioEnabled: true,
    videoEnabled: true,
    roomId: null
  });
  const managerRef = useRef(null);
  const dataListeners = useRef(/* @__PURE__ */ new Set());
  const openListeners = useRef(/* @__PURE__ */ new Set());
  useEffect(() => {
    const mgr = new RTCSessionManager(participantId, signalingAdapter, callConfig);
    managerRef.current = mgr;
    const offs = [
      mgr.on("participantJoined", (p) => dispatch({ type: "PARTICIPANT_JOINED", participant: p })),
      mgr.on("participantUpdated", (p) => {
        console.log("[WebRTC] participantUpdated:", p.id, "streams:", p.streams.length, "connState:", p.connectionState);
        dispatch({ type: "PARTICIPANT_UPDATED", participant: p });
      }),
      mgr.on("participantLeft", (id) => dispatch({ type: "PARTICIPANT_LEFT", id })),
      mgr.on("sharedSourceAdded", (s) => dispatch({ type: "SHARED_SOURCE_ADDED", source: s })),
      mgr.on("sharedSourceRemoved", (id) => dispatch({ type: "SHARED_SOURCE_REMOVED", id })),
      mgr.on("streamAdded", (id, _stream) => {
        console.log("[WebRTC] streamAdded from", id);
      }),
      mgr.on("error", (err) => {
        console.error("[WebRTC] session error:", err);
      }),
      mgr.on("dataMessage", (id, data) => dataListeners.current.forEach((l) => l(id, data))),
      mgr.on("dataChannelOpen", (id) => openListeners.current.forEach((l) => l(id)))
    ];
    return () => {
      offs.forEach((off) => off());
      mgr.destroy();
      managerRef.current = null;
    };
  }, [participantId, signalingAdapter]);
  useEffect(() => {
    const stream = managerRef.current?.localStream$ ?? null;
    dispatch({ type: "SET_LOCAL_STREAM", stream });
  }, [callState.roomId]);
  const joinRoom = useCallback(
    async (roomId, displayName, role = "participant", localStream) => {
      const mgr = managerRef.current;
      if (!mgr) throw new Error("WebRTCProvider not mounted");
      if (role === "host") {
        await signalingAdapter.createRoom(roomId, participantId);
      }
      await mgr.joinRoom(roomId, displayName, role, localStream);
      dispatch({ type: "SET_LOCAL_STREAM", stream: mgr.localStream$ });
      dispatch({ type: "SET_ROOM", roomId });
    },
    [participantId, signalingAdapter]
  );
  const leaveRoom = useCallback(async () => {
    await managerRef.current?.leaveRoom();
    dispatch({ type: "SET_ROOM", roomId: null });
    dispatch({ type: "SET_LOCAL_STREAM", stream: null });
    dispatch({ type: "PARTICIPANT_LEFT", id: "" });
  }, []);
  const toggleAudio = useCallback(() => {
    const next = !callState.audioEnabled;
    managerRef.current?.setAudioEnabled(next);
    dispatch({ type: "SET_AUDIO", enabled: next });
  }, [callState.audioEnabled]);
  const toggleVideo = useCallback(() => {
    const next = !callState.videoEnabled;
    managerRef.current?.setVideoEnabled(next);
    dispatch({ type: "SET_VIDEO", enabled: next });
  }, [callState.videoEnabled]);
  const setLocalStream = useCallback(async (stream) => {
    await managerRef.current?.setLocalStream(stream);
    dispatch({ type: "SET_LOCAL_STREAM", stream });
  }, []);
  const addSharedSource = useCallback(
    async (sourceId, kind, stream, label) => {
      await managerRef.current?.addSharedSource(sourceId, kind, stream, label);
    },
    []
  );
  const removeSharedSource = useCallback(async (sourceId) => {
    await managerRef.current?.removeSharedSource(sourceId);
  }, []);
  const sendData = useCallback((data, to) => managerRef.current?.sendData(data, to) ?? 0, []);
  const onData = useCallback((listener) => {
    dataListeners.current.add(listener);
    return () => {
      dataListeners.current.delete(listener);
    };
  }, []);
  const onDataChannelOpen = useCallback((listener) => {
    openListeners.current.add(listener);
    return () => {
      openListeners.current.delete(listener);
    };
  }, []);
  const createGuestToken2 = useCallback(async () => {
    if (!callState.roomId) throw new Error("Not in a room");
    return signalingAdapter.createGuestToken(callState.roomId);
  }, [callState.roomId, signalingAdapter]);
  const value = useMemo(
    () => ({
      localStream: callState.localStream,
      participants: callState.participants,
      sharedSources: callState.sharedSources,
      audioEnabled: callState.audioEnabled,
      videoEnabled: callState.videoEnabled,
      joinRoom,
      leaveRoom,
      toggleAudio,
      toggleVideo,
      setLocalStream,
      addSharedSource,
      removeSharedSource,
      sessionManager: managerRef.current,
      createGuestToken: createGuestToken2,
      sendData,
      onData,
      onDataChannelOpen
    }),
    [
      callState.localStream,
      callState.participants,
      callState.sharedSources,
      callState.audioEnabled,
      callState.videoEnabled,
      joinRoom,
      leaveRoom,
      toggleAudio,
      toggleVideo,
      setLocalStream,
      addSharedSource,
      removeSharedSource,
      createGuestToken2,
      sendData,
      onData,
      onDataChannelOpen
    ]
  );
  return /* @__PURE__ */ jsx(WebRTCContext.Provider, { value, children });
}
function useWebRTCContext() {
  const ctx = useContext(WebRTCContext);
  if (!ctx) throw new Error("useWebRTCContext must be used inside <WebRTCProvider>");
  return ctx;
}

// src/react/hooks/useWebRTC.ts
import { useMemo as useMemo2 } from "react";
function useWebRTC() {
  const ctx = useWebRTCContext();
  const participants = useMemo2(
    () => Array.from(ctx.participants.values()),
    [ctx.participants]
  );
  return {
    localStream: ctx.localStream,
    participants,
    inCall: ctx.localStream !== null,
    audioEnabled: ctx.audioEnabled,
    videoEnabled: ctx.videoEnabled,
    joinRoom: ctx.joinRoom,
    leaveRoom: ctx.leaveRoom,
    toggleAudio: ctx.toggleAudio,
    toggleVideo: ctx.toggleVideo,
    createGuestToken: ctx.createGuestToken,
    setLocalStream: ctx.setLocalStream,
    addSharedSource: ctx.addSharedSource,
    removeSharedSource: ctx.removeSharedSource
  };
}

// src/react/hooks/useScreenShare.ts
import { useState, useCallback as useCallback2, useRef as useRef2 } from "react";
var sourceCounter = 0;
function nextSourceId(kind) {
  return `${kind}-${++sourceCounter}`;
}
function useScreenShare() {
  const ctx = useWebRTCContext();
  const endedHandlers = useRef2(/* @__PURE__ */ new Map());
  const [, forceUpdate] = useState(0);
  const rerender = useCallback2(() => forceUpdate((n) => n + 1), []);
  const shareStream = useCallback2(
    async (stream, kind, label) => {
      const id = nextSourceId(kind);
      await ctx.addSharedSource(id, kind, stream, label);
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const onEnded = () => {
          void ctx.removeSharedSource(id);
          endedHandlers.current.delete(id);
          rerender();
        };
        videoTrack.addEventListener("ended", onEnded, { once: true });
        endedHandlers.current.set(id, onEnded);
      }
      rerender();
      return id;
    },
    [ctx, rerender]
  );
  const startScreenShare = useCallback2(
    async (label = "Screen") => {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 }, displaySurface: "monitor" },
        audio: false
      });
      return shareStream(stream, "screen", label);
    },
    [shareStream]
  );
  const startCanvasShare = useCallback2(
    async (canvas, frameRate = 30, label = "Canvas") => {
      const stream = canvas.captureStream(frameRate);
      return shareStream(stream, "canvas", label);
    },
    [shareStream]
  );
  const stopSharing = useCallback2(
    async (sourceId) => {
      await ctx.removeSharedSource(sourceId);
      endedHandlers.current.delete(sourceId);
      rerender();
    },
    [ctx, rerender]
  );
  const stopAllSharing = useCallback2(async () => {
    const ids = ctx.sharedSources.map((s) => s.id);
    await Promise.all(ids.map((id) => ctx.removeSharedSource(id)));
    endedHandlers.current.clear();
    rerender();
  }, [ctx, rerender]);
  return {
    sharedSources: ctx.sharedSources,
    isSharing: ctx.sharedSources.length > 0,
    startScreenShare,
    startCanvasShare,
    shareStream,
    stopSharing,
    stopAllSharing
  };
}

// src/react/hooks/useMediaDevices.ts
import { useState as useState2, useEffect as useEffect2, useCallback as useCallback3 } from "react";
function useMediaDevices() {
  const ctx = useWebRTCContext();
  const [cameras, setCameras] = useState2([]);
  const [microphones, setMicrophones] = useState2([]);
  const [speakers, setSpeakers] = useState2([]);
  const [selectedCameraId, setSelectedCameraId] = useState2(null);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState2(null);
  const refresh = useCallback3(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    setCameras(
      devices.filter((d) => d.kind === "videoinput").map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 6)}`, kind: "videoinput" }))
    );
    setMicrophones(
      devices.filter((d) => d.kind === "audioinput").map((d) => ({ deviceId: d.deviceId, label: d.label || `Mic ${d.deviceId.slice(0, 6)}`, kind: "audioinput" }))
    );
    setSpeakers(
      devices.filter((d) => d.kind === "audiooutput").map((d) => ({ deviceId: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 6)}`, kind: "audiooutput" }))
    );
  }, []);
  useEffect2(() => {
    void refresh();
    navigator.mediaDevices.addEventListener("devicechange", refresh);
    return () => navigator.mediaDevices.removeEventListener("devicechange", refresh);
  }, [refresh]);
  useEffect2(() => {
    if (!ctx.localStream) return;
    const video = ctx.localStream.getVideoTracks()[0];
    const audio = ctx.localStream.getAudioTracks()[0];
    if (video) setSelectedCameraId(video.getSettings().deviceId ?? null);
    if (audio) setSelectedMicrophoneId(audio.getSettings().deviceId ?? null);
  }, [ctx.localStream]);
  const selectCamera = useCallback3(
    async (deviceId) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false
      });
      const audio = ctx.localStream?.getAudioTracks()[0];
      if (audio) stream.addTrack(audio);
      await ctx.setLocalStream(stream);
      setSelectedCameraId(deviceId);
    },
    [ctx]
  );
  const selectMicrophone = useCallback3(
    async (deviceId) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false
      });
      const video = ctx.localStream?.getVideoTracks()[0];
      if (video) stream.addTrack(video);
      await ctx.setLocalStream(stream);
      setSelectedMicrophoneId(deviceId);
    },
    [ctx]
  );
  return {
    cameras,
    microphones,
    speakers,
    selectedCameraId,
    selectedMicrophoneId,
    selectCamera,
    selectMicrophone,
    refresh
  };
}

// src/react/components/VideoTile.tsx
import { useEffect as useEffect3, useRef as useRef3 } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function VideoTile({
  participant,
  mirror,
  showName = true,
  showMuteIndicator = true,
  className,
  style,
  placeholder
}) {
  const videoRef = useRef3(null);
  const primaryStream = participant.streams[0] ?? null;
  const hasVideo = primaryStream !== null && participant.videoEnabled;
  useEffect3(() => {
    const el = videoRef.current;
    if (!el) return;
    if (primaryStream) {
      el.srcObject = primaryStream;
    } else {
      el.srcObject = null;
    }
  }, [primaryStream]);
  const shouldMirror = mirror ?? participant.isLocal;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className,
      style: {
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#1a1a1a",
        borderRadius: 12,
        ...style
      },
      children: [
        hasVideo ? /* @__PURE__ */ jsx2(
          "video",
          {
            ref: videoRef,
            autoPlay: true,
            playsInline: true,
            muted: participant.isLocal,
            style: {
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: shouldMirror ? "scaleX(-1)" : "none"
            }
          }
        ) : /* @__PURE__ */ jsx2(
          "div",
          {
            style: {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            children: placeholder ?? /* @__PURE__ */ jsx2(
              "div",
              {
                style: {
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  color: "#fff"
                },
                children: participant.displayName.charAt(0).toUpperCase()
              }
            )
          }
        ),
        showName && /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              position: "absolute",
              bottom: 8,
              left: 8,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              padding: "2px 8px",
              borderRadius: 100,
              fontSize: 12,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 4,
              pointerEvents: "none"
            },
            children: [
              showMuteIndicator && !participant.audioEnabled && /* @__PURE__ */ jsx2(MutedIcon, {}),
              participant.displayName,
              participant.isGuest && /* @__PURE__ */ jsx2("span", { style: { opacity: 0.7, fontSize: 10 }, children: " (guest)" })
            ]
          }
        ),
        showMuteIndicator && !participant.audioEnabled && !participant.isLocal && /* @__PURE__ */ jsx2(
          "div",
          {
            style: {
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(220,53,69,0.85)",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            children: /* @__PURE__ */ jsx2(MutedIcon, { color: "#fff" })
          }
        )
      ]
    }
  );
}
function MutedIcon({ color = "#f00" }) {
  return /* @__PURE__ */ jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [
    /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M12 2a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4Z",
        fill: color,
        opacity: 0.4
      }
    ),
    /* @__PURE__ */ jsx2("line", { x1: "4", y1: "4", x2: "20", y2: "20", stroke: color, strokeWidth: "2.5", strokeLinecap: "round" })
  ] });
}

// src/react/components/ScreenShareTile.tsx
import { useEffect as useEffect4, useRef as useRef4 } from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function isSharedSource(s) {
  return "stream" in s && "kind" in s;
}
function ScreenShareTile({
  source,
  label,
  className,
  style,
  onStop
}) {
  const videoRef = useRef4(null);
  const stream = isSharedSource(source) ? source.stream : source;
  const displayLabel = label ?? (isSharedSource(source) ? source.label : void 0) ?? "Shared screen";
  useEffect4(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream;
  }, [stream]);
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className,
      style: {
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#000",
        borderRadius: 8,
        ...style
      },
      children: [
        /* @__PURE__ */ jsx3(
          "video",
          {
            ref: videoRef,
            autoPlay: true,
            playsInline: true,
            muted: true,
            style: { width: "100%", height: "100%", objectFit: "contain" }
          }
        ),
        /* @__PURE__ */ jsxs2(
          "div",
          {
            style: {
              position: "absolute",
              top: 8,
              left: 8,
              right: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pointerEvents: onStop ? "auto" : "none"
            },
            children: [
              /* @__PURE__ */ jsx3(
                "span",
                {
                  style: {
                    backgroundColor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: 100,
                    fontSize: 12,
                    fontWeight: 500
                  },
                  children: displayLabel
                }
              ),
              onStop && /* @__PURE__ */ jsx3(
                "button",
                {
                  onClick: onStop,
                  style: {
                    backgroundColor: "rgba(220,53,69,0.9)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 100,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer"
                  },
                  children: "Stop sharing"
                }
              )
            ]
          }
        )
      ]
    }
  );
}

// src/react/components/CallLayout.tsx
import { useState as useState3 } from "react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function CallLayout({
  localParticipant,
  participants,
  sharedSources = [],
  mode = "grid",
  renderParticipant,
  onStopSharing,
  className,
  style
}) {
  const [spotlightId, setSpotlightId] = useState3(null);
  const all = [
    ...localParticipant ? [localParticipant] : [],
    ...participants
  ];
  if (mode === "spotlight") {
    const spotlight = all.find((p) => p.id === spotlightId) ?? all[0] ?? null;
    const strip = all.filter((p) => p.id !== spotlight?.id);
    return /* @__PURE__ */ jsxs3(
      "div",
      {
        className,
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 8,
          ...style
        },
        children: [
          /* @__PURE__ */ jsx4("div", { style: { flex: 1, overflow: "hidden" }, children: sharedSources.length > 0 ? /* @__PURE__ */ jsx4(
            ScreenShareTile,
            {
              source: sharedSources[0],
              style: { width: "100%", height: "100%" },
              onStop: onStopSharing ? () => onStopSharing(sharedSources[0].id) : void 0
            }
          ) : spotlight ? renderParticipant ? renderParticipant(spotlight, { width: "100%", height: "100%" }) : /* @__PURE__ */ jsx4(
            VideoTile,
            {
              participant: spotlight,
              style: { width: "100%", height: "100%" }
            }
          ) : null }),
          strip.length > 0 && /* @__PURE__ */ jsx4(
            "div",
            {
              style: {
                display: "flex",
                gap: 8,
                height: 100,
                overflowX: "auto",
                flexShrink: 0
              },
              children: strip.map((p) => /* @__PURE__ */ jsx4(
                "div",
                {
                  onClick: () => setSpotlightId(p.id),
                  style: {
                    flexShrink: 0,
                    width: 160,
                    cursor: "pointer",
                    borderRadius: 8,
                    outline: p.id === spotlightId ? "2px solid #0088ff" : "none",
                    overflow: "hidden"
                  },
                  children: renderParticipant ? renderParticipant(p, { width: "160px", height: "100px" }) : /* @__PURE__ */ jsx4(VideoTile, { participant: p, style: { width: 160, height: 100 } })
                },
                p.id
              ))
            }
          )
        ]
      }
    );
  }
  const totalTiles = all.length + sharedSources.length;
  const cols = totalTiles <= 1 ? 1 : totalTiles <= 4 ? 2 : totalTiles <= 9 ? 3 : 4;
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className,
      style: {
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 8,
        height: "100%",
        ...style
      },
      children: [
        sharedSources.map((src) => /* @__PURE__ */ jsx4(
          ScreenShareTile,
          {
            source: src,
            style: { aspectRatio: "16/9" },
            onStop: onStopSharing ? () => onStopSharing(src.id) : void 0
          },
          src.id
        )),
        all.map(
          (p) => renderParticipant ? /* @__PURE__ */ jsx4("div", { children: renderParticipant(p, { width: "100%", height: "100%" }) }, p.id) : /* @__PURE__ */ jsx4(
            VideoTile,
            {
              participant: p,
              style: { aspectRatio: "16/9" }
            },
            p.id
          )
        )
      ]
    }
  );
}
export {
  CallLayout,
  DEFAULT_ICE_SERVERS,
  FirebaseSignalingAdapter,
  RTCSessionManager,
  ScreenShareTile,
  VideoTile,
  WebRTCProvider,
  WebSocketSignalingAdapter,
  createGuestToken,
  useMediaDevices,
  useScreenShare,
  useWebRTC,
  useWebRTCContext,
  validateGuestToken
};
//# sourceMappingURL=index.js.map